/**
 * inventory_helper.js
 *
 * Centralized constants, styles, and utilities for the Inventory / Project / Configuration UI.
 * Single source of truth for:
 *   - Design tokens (COLOR, INVENTORY_COLORS, SECTION_COLORS)
 *   - Unit status styles & segment builders
 *   - Category & priority tag styles
 *   - Field metadata  (SECTION_ICON_MAP, FIELD_HINTS, AREA_FIELD_KEYS, BALCONY_FIELD_KEYS)
 *   - Select option sets (status, facing, tags, area units, construction status, etc.)
 *   - Default schema / pricing / spec data
 *   - Date rules by construction status
 *   - MUI field sx preset (FIELD_SX) + SECTION_ACCENTS + UI card styles
 *   - formatPriceReadable()
 *   - SectionHeader component
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

import ApartmentIcon                  from '@mui/icons-material/Apartment';
import SquareFootIcon                 from '@mui/icons-material/SquareFoot';
import MeetingRoomIcon                from '@mui/icons-material/MeetingRoom';
import PaymentsIcon                   from '@mui/icons-material/Payments';
import BalconyIcon                    from '@mui/icons-material/Balcony';
import HomeWorkIcon                   from '@mui/icons-material/HomeWork';
import GavelIcon                      from '@mui/icons-material/Gavel';
import LandscapeIcon                  from '@mui/icons-material/Landscape';
import WarehouseIcon                  from '@mui/icons-material/Warehouse';
import BusinessIcon                   from '@mui/icons-material/Business';
import AgricultureIcon                from '@mui/icons-material/Agriculture';
import ElectricalServicesIcon         from '@mui/icons-material/ElectricalServices';
import InfoOutlinedIcon               from '@mui/icons-material/InfoOutlined';
import CurrencyRupeeIcon              from '@mui/icons-material/CurrencyRupee';
import PercentIcon                    from '@mui/icons-material/Percent';
import LocalOfferOutlinedIcon         from '@mui/icons-material/LocalOfferOutlined';
import LocationOnOutlinedIcon         from '@mui/icons-material/LocationOnOutlined';
import CalendarMonthOutlinedIcon      from '@mui/icons-material/CalendarMonthOutlined';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import ImageOutlinedIcon              from '@mui/icons-material/ImageOutlined';
import CheckCircleOutlineIcon         from '@mui/icons-material/CheckCircleOutline';


// ─────────────────────────────────────────────────────────────────────────────
// 1. DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────

/** Raw palette used across Inventory / Unit drawer UI. */
export const COLOR = {
  // Neutrals
  ink:          '#111827',
  inkMid:       '#374151',
  inkLight:     '#6B7280',
  inkXLight:    '#9CA3AF',
  border:       '#E5E7EB',
  surface:      '#FFFFFF',
  surfaceGray:  '#F9FAFB',
  surfaceHover: '#F3F4F6',
  // Brand greens
  green:        '#16a34a',
  greenDark:    '#15803d',
  greenLight:   '#f0fdf4',
  greenBorder:  '#bbf7d0',
  greenText:    '#166534',
  // Semantic
  red:          '#dc2626',
  amber:        '#d97706',
  blue:         '#2563eb',
};

/** Inventory / project accent colors (prefer COLOR above for new code). */
export const INVENTORY_COLORS = {
  green:          '#1a6b3c',
  greenMid:       '#2d8a55',
  greenLight:     '#e8f5ee',
  greenAccent:    '#34c36e',
  gold:           '#d4a017',
  goldBg:         '#faf3e0',
  goldBorder:     '#e8c96a',
  goldText:       '#92610a',
  revenueOrange:  '#ea580c',
  filterChipBlue: '#2563eb',
  filterChipBg:   'rgba(59,130,246,0.12)',
  clearAllRed:    '#dc2626',
};

/** Section-level semantic colors (drives SectionHeader accent variants). */
export const SECTION_COLORS = {
  green:       '#2e7d32',
  greenLight:  'rgba(46,125,50,0.10)',
  greenBg:     'rgba(46,125,50,0.04)',
  blue:        '#1565c0',
  blueLight:   'rgba(21,101,192,0.10)',
  amber:       '#e65100',
  amberLight:  'rgba(230,81,0,0.07)',
  purple:      '#6a1b9a',
  purpleLight: 'rgba(106,27,154,0.09)',
  teal:        '#00695c',
  tealLight:   'rgba(0,105,92,0.09)',
  text:        '#1a1a1a',
  sub:         '#666',
};


// ─────────────────────────────────────────────────────────────────────────────
// 2. UNIT STATUS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Single source of truth for unit availability status.
 * Used in: Overview inventory bar/cards, Configuration table, AddNewUnit drawer.
 */
export const UNIT_STATUS = {
  available: { key: 'available', label: 'Available', labelShort: 'Avail', color: '#22c55e', bgColor: 'rgba(34,197,94,0.12)',    dot: '#22c55e' },
  booked:    { key: 'booked',    label: 'Booked',    labelShort: 'Book',  color: '#2563eb', bgColor: 'rgba(37,99,235,0.1)',      dot: '#2563eb' },
  blocked:   { key: 'blocked',   label: 'Blocked',   labelShort: 'Blkd',  color: '#d97706', bgColor: 'rgba(217,119,6,0.1)',      dot: '#d97706' },
  hold:      { key: 'hold',      label: 'On Hold',   labelShort: 'Hold',  color: '#f97316', bgColor: 'rgba(249,115,22,0.1)',     dot: '#f97316' },
  sold:      { key: 'sold',      label: 'Sold',      labelShort: 'Sold',  color: '#6b7280', bgColor: 'rgba(107,114,128,0.1)',    dot: '#6b7280' },
};

/** Ordered keys for rendering status legend / progress bars. */
export const UNIT_STATUS_KEYS = ['available', 'booked', 'blocked', 'hold', 'sold'];

/**
 * Select options for the unit status dropdown.
 * Derived from UNIT_STATUS so colors stay in sync everywhere.
 */
export const UNIT_STATUS_OPTIONS = UNIT_STATUS_KEYS.map((k) => ({
  value: UNIT_STATUS[k].key,
  label: UNIT_STATUS[k].label,
  dot:   UNIT_STATUS[k].dot,
}));

/**
 * Build status segments for inventory progress bar and summary cards.
 * Returns: [{ label, labelShort, value, pct, color, bgColor }, ...]
 */
export function getUnitStatusSegments(total, available = 0, booked = 0, hold = 0, sold = null) {
  const derivedSold = sold ?? Math.max(0, (total || 0) - available - booked - hold);
  const t = total || 1;
  return [
    { ...UNIT_STATUS.available, value: available,  pct: Math.round((available  / t) * 100) },
    { ...UNIT_STATUS.booked,    value: booked,      pct: Math.round((booked      / t) * 100) },
    { ...UNIT_STATUS.hold,      value: hold,        pct: Math.round((hold        / t) * 100) },
    { ...UNIT_STATUS.sold,      value: derivedSold, pct: Math.round((derivedSold / t) * 100) },
  ];
}

/** Same as getUnitStatusSegments but pre-filtered to non-zero segments (for bar rendering). */
export function getUnitStatusSegmentsForBar(total, available = 0, booked = 0, hold = 0, sold = null) {
  return getUnitStatusSegments(total, available, booked, hold, sold).filter((s) => s.value > 0);
}


// ─────────────────────────────────────────────────────────────────────────────
// 3. CATEGORY & TAG STYLES
// ─────────────────────────────────────────────────────────────────────────────

export const CATEGORY_STYLES = {
  Residential: { bg: 'rgba(34,197,94,0.12)',   color: '#166534', border: 'rgba(34,197,94,0.3)'   },
  Commercial:  { bg: 'rgba(59,130,246,0.12)',  color: '#1d4ed8', border: 'rgba(59,130,246,0.3)'  },
  Plot:        { bg: 'rgba(249,115,22,0.12)',  color: '#c2410c', border: 'rgba(249,115,22,0.3)'  },
  Farmhouse:   { bg: 'rgba(168,85,247,0.12)',  color: '#6b21a8', border: 'rgba(168,85,247,0.3)'  },
  Warehouse:   { bg: 'rgba(107,114,128,0.12)', color: '#374151', border: 'rgba(107,114,128,0.3)' },
  'Farm Land': { bg: 'rgba(34,197,94,0.1)',    color: '#15803d', border: 'rgba(34,197,94,0.25)'  },
  Other:       { bg: 'rgba(107,114,128,0.12)', color: '#374151', border: 'rgba(107,114,128,0.3)' },
};

export function getCategoryStyle(category) {
  return CATEGORY_STYLES[category] || CATEGORY_STYLES.Other;
}

export const PRIORITY_TAG_STYLES = {
  'High Priority':   { dot: '#dc2626', bg: 'rgba(254,226,226,0.9)', color: '#b91c1c' },
  'Medium Priority': { dot: '#d97706', bg: 'rgba(254,243,199,0.9)', color: '#92400e' },
  'Low Priority':    { dot: '#059669', bg: 'rgba(209,250,229,0.9)', color: '#047857' },
};

const DEFAULT_TAG_STYLE = { dot: '#6b7280', bg: 'rgba(243,244,246,0.9)', color: '#374151' };

/** Returns style for a project header tag (priority label or category name). */
export function getTagStyle(tag) {
  if (PRIORITY_TAG_STYLES[tag]) return PRIORITY_TAG_STYLES[tag];
  if (CATEGORY_STYLES[tag]) {
    const cat = CATEGORY_STYLES[tag];
    return { dot: cat.color, bg: cat.bg, color: cat.color };
  }
  return DEFAULT_TAG_STYLE;
}


// ─────────────────────────────────────────────────────────────────────────────
// 4. FIELD METADATA
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Schema section key → icon element.
 * Used in SectionHeader to render contextual icons per section type.
 */
export const SECTION_ICON_MAP = {
  location_placement: <ApartmentIcon          sx={{ fontSize: 14 }} />,
  location_access:    <ApartmentIcon          sx={{ fontSize: 14 }} />,
  area_details:       <SquareFootIcon         sx={{ fontSize: 14 }} />,
  plot_details:       <LandscapeIcon          sx={{ fontSize: 14 }} />,
  land_details:       <AgricultureIcon        sx={{ fontSize: 14 }} />,
  layout:             <MeetingRoomIcon        sx={{ fontSize: 14 }} />,
  office_layout:      <BusinessIcon           sx={{ fontSize: 14 }} />,
  operational_specs:  <WarehouseIcon          sx={{ fontSize: 14 }} />,
  pricing_extras:     <PaymentsIcon           sx={{ fontSize: 14 }} />,
  pricing_lease:      <PaymentsIcon           sx={{ fontSize: 14 }} />,
  amenities:          <BalconyIcon            sx={{ fontSize: 14 }} />,
  utilities:          <ElectricalServicesIcon sx={{ fontSize: 14 }} />,
  property_state:     <HomeWorkIcon           sx={{ fontSize: 14 }} />,
  legal:              <GavelIcon              sx={{ fontSize: 14 }} />,
};

/**
 * Contextual helper text keyed by schema field key.
 * Render below inputs to guide data entry.
 */
export const FIELD_HINTS = {
  tower_wing:            'e.g. Tower A, East Block, Wing 2',
  floor:                 'Ground floor = 0, First floor = 1',
  total_floors:          'Total storeys in the building',
  builtup_area:          'Carpet area including wall thickness',
  balcony_size:          'Combined area of all balconies',
  terrace_area:          'Open terrace on the top — if applicable',
  plot_area:             'Total land extent including setbacks and margins',
  land_area:             'Total land area',
  bhk:                   'Bedrooms + Hall + Kitchen count',
  bathrooms:             'Total count — attached and common combined',
  balconies:             'Number of balconies in the unit',
  floors:                'Number of floors this unit spans',
  premium_charge:        'Extra charge for floor rise, view, or corner position',
  cam_charges:           'Common Area Maintenance — monthly charge per sq.ft',
  lock_in_period:        'Minimum committed tenancy before exit clause activates',
  lease_tenure:          'Total duration of the lease agreement',
  parking:               'Covered / allocated parking slots',
  covered_parking:       'Dedicated covered parking slots',
  road_width:            'Width of the road abutting the plot — affects accessibility & price',
  frontage:              'Width of the shop or unit facing the street',
  clear_height:          'Floor-to-bottom-of-beam clearance — critical for racking systems',
  loading_docks:         'Number of truck bays available for loading and unloading',
  floor_capacity:        'Maximum load the floor can safely bear (kg per sq.ft)',
  water_source:          'Primary water supply available on the land',
  building_grade:        'Grade A+ = premium spec, Grade B/C = functional spec',
  workstations:          'Total open-plan seating capacity',
  distance_from_highway: 'Road distance to the nearest national or state highway',
  age_of_property:       'Enter 0 for new or under-construction units',
  entrance_width:        'Width of main entry — affects vehicle access',
  soil_type:             'Affects foundation type and agricultural usability',
  zoning:                'Approved land use classification',
};

/**
 * Field keys that represent area measurements.
 * Triggers area-unit badge in number inputs and area-specific validation.
 */
export const AREA_FIELD_KEYS = new Set([
  'carpet_area', 'builtup_area', 'super_builtup_area', 'plot_area',
  'total_area', 'area', 'super_area', 'terrace_area', 'balcony_size', 'land_area',
]);

/**
 * Balcony-related field keys — capped at a max value of 10.
 */
export const BALCONY_FIELD_KEYS = new Set([
  'balcony_size', 'balconies', 'balcony_area', 'number_of_balconies',
]);


// ─────────────────────────────────────────────────────────────────────────────
// 5. SELECT OPTION SETS
// ─────────────────────────────────────────────────────────────────────────────

/** Compass facing options (fallback when API doesn't supply them). */
export const FACING_OPTIONS = [
  { value: 'north',  label: 'North'      },
  { value: 'north_east', label: 'North-East' },
  { value: 'east',  label: 'East'       },
  { value: 'south-east', label: 'South-East' },
  { value: 'south',  label: 'South'      },
  { value: 'south_west', label: 'South-West' },
  { value: 'west',  label: 'West'       },
  { value: 'north_west', label: 'North-West' },
];

/** Suggested tags for the unit tags autocomplete. */
export const UNIT_TAG_SUGGESTIONS = [
  'Corner Unit', 'Sea View', 'Pool Facing', 'Park Facing', 'Road Facing',
  'Garden View', 'Furnished', 'Semi-Furnished', 'Premium', 'Top Floor',
  'Ground Floor', 'Duplex', 'East Facing',
];

/** Suggested tags for the configuration tag autocomplete. */
export const CONFIG_TAG_SUGGESTIONS = [
  '2 BHK', '3 BHK', '4 BHK', 'Studio', 'Villa',
  'Premium', 'Luxury', 'Standard', 'Economy',
  'Duplex', 'Penthouse', 'Garden Facing', 'Terrace',
];

/** Property category options (used in configuration form). */
export const CATEGORY_OPTIONS = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial',  label: 'Commercial'  },
  { value: 'office',      label: 'Office'      },
  { value: 'retail',      label: 'Retail'      },
  { value: 'plot',        label: 'Plot'        },
  { value: 'industrial',  label: 'Industrial'  },
];

/** Area unit options. */
export const AREA_UNIT_OPTIONS = [
  { value: 'sqft',   label: 'sq.ft'    },
  { value: 'sqmt',   label: 'sq.mt'    },
  { value: 'sqyard', label: 'sq.yards' },
  { value: 'acres',  label: 'Acres'    },
  { value: 'gaj',    label: 'Gaj'      },
];

/** Construction status options. */
export const CONSTRUCTION_STATUS_OPTIONS = [
  { value: 'pre_launch',         label: 'Pre-Launch'         },
  { value: 'new_launch',         label: 'New Launch'         },
  { value: 'under_construction', label: 'Under Construction' },
  { value: 'near_possession',    label: 'Near Possession'    },
  { value: 'ready_to_move',      label: 'Ready to Move'      },
  { value: 'completed',          label: 'Completed'          },
];

/** Possession status options. */
export const POSSESSION_STATUS_OPTIONS = [
  { value: 'ready_to_move',      label: 'Ready to Move'      },
  { value: 'near_possession',    label: 'Near Possession'    },
  { value: 'under_construction', label: 'Under Construction' },
];

/** Completion status options. */
export const COMPLETION_STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
];


// ─────────────────────────────────────────────────────────────────────────────
// 6. DEFAULT SCHEMA / PRICING DATA
// ─────────────────────────────────────────────────────────────────────────────

/** Default property types grouped by category (fallback when API is unavailable). */
export const DEFAULT_PROPERTY_TYPES_BY_CATEGORY = {
  residential: [
    { id: 'pt-apt',       name: 'Apartment'  },
    { id: 'pt-villa',     name: 'Villa'      },
    { id: 'pt-penthouse', name: 'Penthouse'  },
    { id: 'pt-row',       name: 'Row House'  },
  ],
  commercial: [
    { id: 'pt-co',       name: 'Commercial Office' },
    { id: 'pt-shop',     name: 'Retail Shop'       },
    { id: 'pt-showroom', name: 'Showroom'          },
  ],
  office:     [{ id: 'pt-off',     name: 'Office Space'     }],
  plot:       [{ id: 'pt-plot',    name: 'Residential Plot' }, { id: 'pt-iplot',   name: 'Industrial Plot' }],
  retail:     [{ id: 'pt-rshop',   name: 'Shop'             }, { id: 'pt-kiosk',   name: 'Kiosk'          }],
  industrial: [{ id: 'pt-ware',    name: 'Warehouse'        }, { id: 'pt-factory', name: 'Factory'        }],
};

/** Minimal UI schema used as fallback when no property-type schema is available. */
export const DEFAULT_CONFIG_UI_SCHEMA = {
  sections: [{
    key: 'config_details', label: 'Configuration Details', order: 1,
    fields: [
      { key: 'total_area',  type: 'number', label: 'Total Area'  },
      { key: 'carpet_area', type: 'number', label: 'Carpet Area' },
      { key: 'bedrooms',    type: 'number', label: 'Bedrooms'    },
    ],
  }],
};

/** Default pricing schema line items. */
export const DEFAULT_CONFIG_PRICING_SCHEMA = [
  { label: 'base_price_per_sqft', note: "Builder's base rate per sq.ft" },
  { label: 'parking_charge',      note: 'Per covered parking slot'      },
];

/** Default specification schema line items. */
export const DEFAULT_SPEC_SCHEMA = [
  { label: 'Structure Type', category: 'structure' },
  { label: 'Flooring',       category: 'flooring'  },
  { label: 'Fittings Brand', category: 'bathroom'  },
];


// ─────────────────────────────────────────────────────────────────────────────
// 7. DATE RULES BY CONSTRUCTION STATUS
// ─────────────────────────────────────────────────────────────────────────────

export const CONSTRUCTION_STATUS_DATE_RULES = {
  pre_launch: {
    launch_date:     { visible: true,  required: false, label: 'Expected Launch Date'     },
    possession_date: { visible: false, required: false, label: 'Expected Possession Date' },
    completion_date: { visible: false, required: false, label: 'Expected Completion Date' },
  },
  new_launch: {
    launch_date:     { visible: true,  required: true,  label: 'Launch Date'              },
    possession_date: { visible: false, required: false, label: 'Expected Possession Date' },
    completion_date: { visible: false, required: false, label: 'Expected Completion Date' },
  },
  under_construction: {
    launch_date:     { visible: true,  required: false, label: 'Launch Date'              },
    possession_date: { visible: true,  required: true,  label: 'Expected Possession Date' },
    completion_date: { visible: false, required: false, label: 'Expected Completion Date' },
  },
};


// ─────────────────────────────────────────────────────────────────────────────
// 8. MUI STYLE PRESETS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reusable MUI TextField / FormControl sx for the unit drawer.
 * 42px compact inputs with subtle focus ring.
 */
export const FIELD_SX = {
  '& .MuiInputBase-root': {
    height: 42, fontSize: 13, borderRadius: '8px', bgcolor: COLOR.surface,
    transition: 'box-shadow 0.15s',
    '&:hover fieldset':       { borderColor: '#9CA3AF' },
    '&.Mui-focused fieldset': { borderColor: COLOR.inkMid, borderWidth: '1.5px' },
    '&.Mui-focused':          { boxShadow: '0 0 0 3px rgba(17,24,39,0.05)' },
  },
  '& .MuiInputLabel-root': {
    fontSize: 12.5, color: COLOR.inkLight,
    '&.Mui-focused': { color: COLOR.inkMid },
  },
  '& .MuiInputBase-input':              { fontSize: 13 },
  '& .MuiInputBase-input::placeholder': { opacity: 0.4, fontSize: 12.5 },
  '& .MuiOutlinedInput-root fieldset':  { borderColor: COLOR.border },
};

/** Section accent configs keyed by accent name. Used in SectionHeader. */
export const SECTION_ACCENTS = {
  developer:  { color: SECTION_COLORS.blue,   bg: SECTION_COLORS.blueLight,   icon: <BusinessIcon                   sx={{ fontSize: 13 }} /> },
  info:       { color: SECTION_COLORS.green,  bg: SECTION_COLORS.greenLight,  icon: <InfoOutlinedIcon               sx={{ fontSize: 13 }} /> },
  dates:      { color: SECTION_COLORS.amber,  bg: SECTION_COLORS.amberLight,  icon: <CalendarMonthOutlinedIcon      sx={{ fontSize: 13 }} /> },
  pricing:    { color: SECTION_COLORS.green,  bg: SECTION_COLORS.greenLight,  icon: <CurrencyRupeeIcon              sx={{ fontSize: 13 }} /> },
  commission: { color: SECTION_COLORS.purple, bg: SECTION_COLORS.purpleLight, icon: <PercentIcon                    sx={{ fontSize: 13 }} /> },
  tags:       { color: SECTION_COLORS.teal,   bg: SECTION_COLORS.tealLight,   icon: <LocalOfferOutlinedIcon         sx={{ fontSize: 13 }} /> },
  location:   { color: SECTION_COLORS.blue,   bg: SECTION_COLORS.blueLight,   icon: <LocationOnOutlinedIcon         sx={{ fontSize: 13 }} /> },
  thumbnail:  { color: SECTION_COLORS.amber,  bg: SECTION_COLORS.amberLight,  icon: <ImageOutlinedIcon              sx={{ fontSize: 13 }} /> },
  highlights: { color: SECTION_COLORS.teal,   bg: SECTION_COLORS.tealLight,   icon: <FormatListBulletedOutlinedIcon sx={{ fontSize: 13 }} /> },
  other:      { color: SECTION_COLORS.purple, bg: SECTION_COLORS.purpleLight, icon: <FormatListBulletedOutlinedIcon sx={{ fontSize: 13 }} /> },
};

/** Stat card styles for Configuration page summary row. */
export const STAT_CARD_STYLES = [
  { label: 'Total Configurations', bg: 'rgba(59,130,246,0.1)',   color: '#2563eb' },
  { label: 'Total Units',          bg: 'rgba(34,197,94,0.1)',    color: '#16a34a' },
  { label: 'Available',            bg: 'rgba(34,197,94,0.08)',   color: '#15803d' },
  { label: 'Sold',                 bg: 'rgba(107,114,128,0.12)', color: '#6b7280' },
];

/** Action menu item styles for configuration row actions. */
export const ACTION_MENU_STYLES = [
  { label: 'View Units',          bg: 'rgba(59,130,246,0.15)', color: '#2563eb' },
  { label: 'Edit Configuration',  bg: 'rgba(139,92,246,0.15)', color: '#7c3aed' },
  { label: 'Add Unit',            bg: 'rgba(34,197,94,0.15)',  color: '#16a34a' },
  { label: 'Pause Configuration', bg: 'rgba(249,115,22,0.15)', color: '#ea580c' },
  { label: 'Archive',             bg: 'rgba(239,68,68,0.12)',  color: '#dc2626' },
];

/** Sales velocity card icon styles. */
export const SALES_VELOCITY_ICON_STYLES = {
  units:  { bg: 'rgba(34,197,94,0.15)',  color: '#16a34a' },
  days:   { bg: 'rgba(59,130,246,0.15)', color: '#2563eb' },
  config: { bg: 'rgba(139,92,246,0.15)', color: '#8b5cf6' },
};

/** "Active" status chip style (e.g. on configuration rows). */
export const ACTIVE_STATUS_CHIP = {
  bg:       'rgba(34,197,94,0.12)',
  color:    '#166534',
  border:   'rgba(34,197,94,0.3)',
  dotColor: '#22c55e',
};


// Dot/color map for status values (API returns value/label; we add styles)
export const STATUS_DOT = {
  available: { dot: '#22c55e', color: '#15803d', bg: 'rgba(34,197,94,0.10)', border: 'rgba(34,197,94,0.3)' },
  booked:    { dot: '#f59e0b', color: '#b45309', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.3)' },
  sold:      { dot: '#a78bfa', color: '#6d28d9', bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.3)' },
  blocked:   { dot: '#ef4444', color: '#b91c1c', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.3)' },
  not_available: { dot: '#6b7280', color: '#4b5563', bg: 'rgba(107,114,128,0.12)', border: 'rgba(107,114,128,0.25)' },
};

export const UnitTableStyles = {
  g800: '#1a4731', g700: '#1e5438', g600: '#246040', g50: '#eef7f1',
  g500: '#2d7a52', g200: '#9fd4b5', g100: '#d4edd9',
  mint: '#00c46d', mintSoft: 'rgba(0,196,109,.09)',
  ink: '#0f1f15', ink2: '#1e3a28', muted: '#506459', subtle: '#7a9585',
  border: '#e0ece4', border2: '#c8ddd2',
  surface: '#f4f8f5', surface2: '#ebf4ee', white: '#ffffff',
  red: '#dc2626', redBg: '#fef2f2', redText: '#991b1b', redBorder: '#fca5a5',
  orange: '#ea6c00', orangeBg: '#fff4ec', orangeText: '#7c2d12', orangeBorder: '#fdba74',
  violet: '#7c3aed', violetBg: '#f5f3ff', violetText: '#5b21b6',
  blue: '#2563eb', blueBg: '#eff6ff', blueText: '#1e40af',
  amber: '#f59e0b', amberBg: '#fef3c7', amberText: '#92400e',
};


export const COMMISSION_RATE_INPUT_CONSTRAINTS = {
  min:  0,
  max:  20,
  step: 0.25,
};

export const PRICE_INPUT_CONSTRAINTS = {
  min: 100000,
  max: 1000000000,
  step: 50000,
};

// ─────────────────────────────────────────────────────────────────────────────
// 9. FORMATTERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a raw number as a readable Indian currency string.
 * 7500000 → "₹75 L"  |  12500000 → "₹1.25 Cr"  |  50000 → "₹50,000"
 */
export const formatPriceReadable = (value) => {
  if (!value || isNaN(value)) return null;
  const num = parseFloat(value);

  if (num >= 1_00_00_000) {
    // 1 Crore+
    const cr = num / 1_00_00_000;
    return `${parseFloat(cr.toFixed(2))}Cr`;
  } else if (num >= 1_00_000) {
    // 1 Lakh+
    const l = num / 1_00_000;
    return `${parseFloat(l.toFixed(2))}L`;
  } else if (num >= 1_000) {
    const k = num / 1_000;
    return `${parseFloat(k.toFixed(1))}K`;
  }
  return `${num}`;
};


// ─────────────────────────────────────────────────────────────────────────────
// 10. COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SectionHeader
 * Icon badge + title + optional subtitle row.
 * Pass `complete` to swap the icon for a green checkmark.
 *
 * @param {string}  title
 * @param {string}  [subtitle]
 * @param {string}  [accent='info']  — key of SECTION_ACCENTS
 * @param {boolean} [complete]       — shows green check when all fields are filled
 */
export function SectionHeader({ title, subtitle, accent = 'info', complete }) {
  const a = SECTION_ACCENTS[accent] ?? SECTION_ACCENTS.info;
  return (
    <Box sx={{ mb: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: 1.5, flexShrink: 0, mt: 0.1,
        bgcolor: complete ? SECTION_COLORS.greenLight : a.bg,
        color:   complete ? SECTION_COLORS.green      : a.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: `1.5px solid ${complete ? SECTION_COLORS.green : a.color}22`,
        transition: 'all 0.2s ease',
      }}>
        {complete
          ? <CheckCircleOutlineIcon sx={{ fontSize: 14, color: SECTION_COLORS.green }} />
          : a.icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: SECTION_COLORS.text, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 10.5, color: SECTION_COLORS.sub, display: 'block', lineHeight: 1.4 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}


const DEFAULT_AREA_PRIORITY = ['carpet_area', 'builtup_area', 'super_builtup_area', 'total_area'];

const CATEGORY_AREA_PRIORITY = {
  residential:  ['carpet_area', 'builtup_area', 'super_builtup_area', 'total_area'],
  commercial:   ['builtup_area', 'super_builtup_area', 'carpet_area', 'total_area'],
  retail:       ['builtup_area', 'carpet_area', 'super_builtup_area', 'total_area'],
  office:       ['builtup_area', 'carpet_area', 'super_builtup_area', 'total_area'],
  warehouse:    ['total_area', 'builtup_area', 'super_builtup_area', 'carpet_area'],
  industrial:   ['total_area', 'builtup_area', 'super_builtup_area', 'carpet_area'],
  plot:         ['total_area', 'carpet_area', 'builtup_area', 'super_builtup_area'],
  farm_land:    ['total_area', 'carpet_area', 'builtup_area', 'super_builtup_area'],
  farmhouse:    ['total_area', 'super_builtup_area', 'builtup_area', 'carpet_area'],
  hospitality:  ['builtup_area', 'super_builtup_area', 'carpet_area', 'total_area'],
  mixed_use:    ['builtup_area', 'super_builtup_area', 'carpet_area', 'total_area'],
  other:        ['carpet_area', 'builtup_area', 'super_builtup_area', 'total_area'],
};

/** "carpet_area" -> "Carpet Area" */
function formatAreaLabel(key) {
  if (key == null) return '';
  return String(key)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function pickPrimaryArea(unit, category) {
  const areaArr = Array.isArray(unit.area) ? unit.area : [];
  if (!areaArr.length) return unit.area_list?.[0] ?? null;

  const priority = CATEGORY_AREA_PRIORITY[(category || '').toLowerCase()] ?? DEFAULT_AREA_PRIORITY;

  for (const key of priority) {
    const match = areaArr.find((a) => a.key === key);
    if (match) return `${formatAreaLabel(match.key)}: ${Number(match.value).toLocaleString('en-IN')} ${match.unit || 'sqft'}`;
  }

  // Last resort: first custom key (e.g. "area1")
  const first = areaArr[0];
  return `${formatAreaLabel(first.key)}: ${Number(first.value).toLocaleString('en-IN')} ${first.unit || 'sqft'}`;
}